<template>
  <div>
    <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
      <p class="ck-subtitulo mb-0" style="max-width: 780px">
        Atestados, declarações, férias e outras ausências. Só as justificativas <b>aprovadas</b> entram no espelho de ponto.
        Use <b>parte do dia</b> para consultas e saídas antecipadas: só as horas informadas são abonadas.
      </p>
      <button v-if="podeLancar" class="btn btn-info" @click="novo()"><i class="bi bi-plus-lg"></i> Nova justificativa</button>
    </div>

    <!-- Contadores -->
    <div class="kpis mb-3">
      <button :class="['kpi', 'alerta', { ativo: filtro.status === '0' }]" @click="filtrarStatus('0')">
        <span>Pendentes de aprovação</span><strong>{{ resumo.pendentes ?? '–' }}</strong>
      </button>
      <div class="kpi ok"><span>Aprovadas neste mês</span><strong>{{ resumo.aprovadas_mes ?? '–' }}</strong></div>
      <div class="kpi"><span>Ausentes hoje (justificado)</span><strong>{{ resumo.ausentes_hoje ?? '–' }}</strong></div>
    </div>

    <!-- Filtros -->
    <div class="card mb-3">
      <div class="card-body pb-2">
        <div class="row g-2 align-items-end">
          <div class="col-lg-4 col-md-6">
            <label>Colaborador</label>
            <select id="just-filtro-colab" data-placeholder="Todos" data-allow-clear="true" style="width:100%"></select>
          </div>
          <div class="col-lg-2 col-md-6">
            <label>Tipo</label>
            <select v-model="filtro.tipo" class="form-select" @change="pesquisar">
              <option value="">Todos</option>
              <option v-for="t in tipos" :key="t.id" :value="String(t.id)">{{ t.nome }}</option>
            </select>
          </div>
          <div class="col-lg-2 col-6">
            <label>De</label>
            <input type="date" v-model="filtro.datainicio" class="form-control" @change="pesquisar">
          </div>
          <div class="col-lg-2 col-6">
            <label>Até</label>
            <input type="date" v-model="filtro.datafim" class="form-control" @change="pesquisar">
          </div>
          <div class="col-lg-2">
            <button class="btn btn-light w-100 mb-2" @click="limparFiltros"><i class="bi bi-x-circle"></i> Limpar</button>
          </div>
        </div>
        <div class="btn-group mt-1 mb-2 status-filtro" role="group">
          <button v-for="s in STATUS_FILTRO" :key="s.v" type="button" :class="['btn btn-sm', filtro.status === s.v ? 'btn-info' : 'btn-light']"
                  @click="filtrarStatus(s.v)">{{ s.t }}</button>
        </div>
      </div>
    </div>

    <!-- Lista -->
    <div class="card">
      <div v-if="carregando" class="text-center py-5"><span class="spinner-border"></span></div>
      <div v-else-if="!lista.length" class="text-center text-muted py-5">
        <i class="bi bi-journal-check" style="font-size: 2rem"></i>
        <p class="mt-2 mb-0">Nenhuma justificativa encontrada.</p>
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover align-middle mb-0 lista">
          <thead>
            <tr><th>Colaborador</th><th>Tipo</th><th>Período</th><th class="text-end">Abona</th><th>Situação</th><th style="width: 170px"></th></tr>
          </thead>
          <tbody>
            <tr v-for="j in lista" :key="j.id" :class="{ cancelada: j.status === 2 }" @click="abrir(j)">
              <td>
                <div class="fw-semibold">{{ j.nomecolaborador }}</div>
                <small class="text-muted text-truncate d-inline-block" style="max-width: 260px" :title="j.obs">{{ j.obs }}</small>
              </td>
              <td>
                <span class="tipo">{{ j.tipo_nome }}</span>
                <span v-if="!j.abona" class="badge text-bg-light ms-1" title="Não conta como falta, mas as horas continuam devidas">sem abono</span>
              </td>
              <td class="text-nowrap">
                <template v-if="j.hora_inicio">
                  {{ dataBR(j.dtjustificativa) }} <small class="text-muted">{{ j.hora_inicio }}–{{ j.hora_fim }}</small>
                </template>
                <template v-else-if="j.dtfimjustificativa !== j.dtjustificativa">
                  {{ dataCurta(j.dtjustificativa) }} a {{ dataBR(j.dtfimjustificativa) }}
                  <div><small class="text-muted">{{ j.dias_corridos }} dias · {{ j.dias_uteis }} de trabalho</small></div>
                </template>
                <template v-else>{{ dataBR(j.dtjustificativa) }}</template>
              </td>
              <td class="text-end num">{{ j.abona ? j.abonadas : '—' }}</td>
              <td>
                <span :class="['badge', STATUS[j.status].classe]">{{ STATUS[j.status].texto }}</span>
                <i v-if="j.arquivos.length" class="bi bi-paperclip ms-1 text-muted" :title="j.arquivos.length + ' anexo(s)'"></i>
              </td>
              <td class="text-end text-nowrap" @click.stop>
                <template v-if="j.status === 0 && podeAprovar">
                  <button class="btn btn-sm btn-success" @click="aprovar(j)" title="Aprovar"><i class="bi bi-check-lg"></i> Aprovar</button>
                  <button class="btn btn-sm btn-light text-danger ms-1" @click="cancelar(j, true)" title="Recusar"><i class="bi bi-x-lg"></i></button>
                </template>
                <button v-else-if="j.status === 1 && podeAprovar" class="btn btn-sm btn-light" @click="cancelar(j)" title="Cancelar justificativa">
                  <i class="bi bi-arrow-counterclockwise"></i> Cancelar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="total > porPagina" class="card-footer d-flex justify-content-between align-items-center small">
        <span class="text-muted">{{ (pagina - 1) * porPagina + 1 }}–{{ Math.min(pagina * porPagina, total) }} de {{ total }}</span>
        <div class="btn-group">
          <button class="btn btn-sm btn-light" :disabled="pagina === 1" @click="carregar(pagina - 1)"><i class="bi bi-chevron-left"></i></button>
          <button class="btn btn-sm btn-light" :disabled="pagina * porPagina >= total" @click="carregar(pagina + 1)"><i class="bi bi-chevron-right"></i></button>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div class="modal fade" ref="modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ form.id ? 'Justificativa #' + form.id : 'Nova justificativa' }}
              <span v-if="form.id" :class="['badge ms-2 align-middle', STATUS[form.status].classe]">{{ STATUS[form.status].texto }}</span>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <div class="row g-4">
              <!-- Dados -->
              <div class="col-lg-7">
                <div class="row g-2">
                  <div class="col-md-7">
                    <label>Colaborador</label>
                    <select id="just-colab" data-placeholder="Selecione o colaborador" style="width:100%" :disabled="!editavel"></select>
                  </div>
                  <div class="col-md-5">
                    <label>Tipo</label>
                    <select v-model.number="form.tipo" class="form-select" :disabled="!editavel" @change="atualizarPrevia">
                      <option v-for="t in tipos" :key="t.id" :value="t.id">{{ t.nome }}</option>
                    </select>
                    <small class="text-muted">{{ tipoAtual && tipoAtual.abona ? 'Abona as horas do período.' : 'Não conta como falta, mas as horas continuam devidas.' }}</small>
                  </div>

                  <div class="col-12 mt-2">
                    <div class="btn-group w-100" role="group">
                      <button type="button" :disabled="!editavel" :class="['btn', !form.parcial ? 'btn-info' : 'btn-light']" @click="setParcial(false)">
                        <i class="bi bi-calendar-range"></i> Dia inteiro / período
                      </button>
                      <button type="button" :disabled="!editavel" :class="['btn', form.parcial ? 'btn-info' : 'btn-light']" @click="setParcial(true)">
                        <i class="bi bi-clock"></i> Parte do dia
                      </button>
                    </div>
                  </div>

                  <template v-if="!form.parcial">
                    <div class="col-6"><label>De</label><input type="date" v-model="form.dtjustificativa" :disabled="!editavel" class="form-control" @change="ajustarFim"></div>
                    <div class="col-6"><label>Até</label><input type="date" v-model="form.dtfimjustificativa" :min="form.dtjustificativa" :disabled="!editavel" class="form-control" @change="atualizarPrevia"></div>
                    <div class="col-12" v-if="editavel">
                      <div class="atalhos">
                        <span class="small text-muted">Duração:</span>
                        <button v-for="n in [1, 2, 3, 5, 15, 30]" :key="n" type="button" class="btn btn-sm btn-light" @click="duracao(n)">{{ n }} {{ n === 1 ? 'dia' : 'dias' }}</button>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="col-md-4"><label>Data</label><input type="date" v-model="form.dtjustificativa" :disabled="!editavel" class="form-control" @change="atualizarPrevia"></div>
                    <div class="col-6 col-md-4"><label>Das</label><input type="time" v-model="form.hora_inicio" :disabled="!editavel" class="form-control" @change="atualizarPrevia"></div>
                    <div class="col-6 col-md-4"><label>Às</label><input type="time" v-model="form.hora_fim" :disabled="!editavel" class="form-control" @change="atualizarPrevia"></div>
                  </template>

                  <div class="col-12">
                    <label>Motivo / observação</label>
                    <textarea v-model.trim="form.obs" rows="2" maxlength="255" class="form-control" :disabled="!editavel"
                              placeholder="Ex.: CID não obrigatório. Atestado de 3 dias do Dr. Fulano, consulta odontológica..."></textarea>
                  </div>

                  <!-- Anexos -->
                  <div class="col-12">
                    <label>Anexos <small class="text-muted fw-normal">(imagem ou PDF, até 8 MB)</small></label>
                    <div class="anexos">
                      <div v-for="a in arquivos" :key="a.patharquivo" class="anexo">
                        <a :href="urlArquivo(a.patharquivo)" target="_blank" class="miniatura" :title="a.arquivo">
                          <img v-if="ehImagem(a.patharquivo)" :src="urlArquivo(a.patharquivo)" alt="">
                          <i v-else class="bi bi-file-earmark-pdf"></i>
                        </a>
                        <button v-if="editavel" type="button" class="btn-remover" title="Remover" @click="removerArquivo(a)"><i class="bi bi-x"></i></button>
                      </div>
                      <label v-if="editavel" class="anexo adicionar" :class="{ enviando }">
                        <input type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" @change="enviarArquivos" hidden>
                        <span v-if="enviando" class="spinner-border spinner-border-sm"></span>
                        <template v-else><i class="bi bi-cloud-arrow-up"></i><small>Adicionar</small></template>
                      </label>
                      <span v-if="!editavel && !arquivos.length" class="small text-muted">Sem anexos.</span>
                    </div>
                    <small v-if="editavel && form.tipo === 0 && !arquivos.length" class="text-warning-emphasis">
                      <i class="bi bi-exclamation-triangle"></i> Recomendado anexar o atestado.
                    </small>
                  </div>

                  <!-- Auditoria -->
                  <div class="col-12" v-if="form.id">
                    <ul class="auditoria">
                      <li v-if="form.dh_registro"><i class="bi bi-pencil"></i> Lançada {{ form.usuario ? 'por ' + form.usuario : '' }} em {{ dataHora(form.dh_registro) }}</li>
                      <li v-if="form.dtefetivacao"><i class="bi bi-check2-circle"></i> Aprovada {{ form.usuario_efetivacao ? 'por ' + form.usuario_efetivacao : '' }} em {{ dataHora(form.dtefetivacao) }}</li>
                      <li v-if="form.dtcancelamento" class="text-danger">
                        <i class="bi bi-x-circle"></i> Cancelada {{ form.usuario_cancelamento ? 'por ' + form.usuario_cancelamento : '' }} em {{ dataHora(form.dtcancelamento) }}
                        <span v-if="form.motivo_cancelamento">: {{ form.motivo_cancelamento }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Prévia -->
              <div class="col-lg-5">
                <div class="previa">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong>O que entra no espelho</strong>
                    <span v-if="carregandoPrevia" class="spinner-border spinner-border-sm text-muted"></span>
                  </div>
                  <div v-if="!previa" class="small text-muted">Escolha o colaborador e o período para ver os dias afetados.</div>
                  <template v-else>
                    <div v-if="previa.conflitos && previa.conflitos.length" class="alert alert-danger py-2 small mb-2">
                      <b>Já existe justificativa neste período:</b>
                      <div v-for="c in previa.conflitos" :key="c">{{ c }}</div>
                    </div>
                    <div class="totais">
                      <div><span>Dias</span><strong>{{ previa.dias_corridos }}</strong></div>
                      <div><span>De trabalho</span><strong>{{ previa.dias_uteis }}</strong></div>
                      <div><span>Horas abonadas</span><strong>{{ previa.abona ? previa.abonadas : '—' }}</strong></div>
                    </div>
                    <div v-if="form.parcial && previa.abona && previa.minutos_abonados === 0 && form.hora_inicio && form.hora_fim" class="small text-warning-emphasis mt-2">
                      <i class="bi bi-exclamation-triangle"></i> O horário informado não cruza com o horário previsto do dia.
                    </div>
                    <ul class="dias">
                      <li v-for="d in previa.dias.slice(0, 31)" :key="d.data" :class="{ folga: !d.util }">
                        <span class="d">{{ dataCurta(d.data) }} <small>{{ d.dia.substring(0, 3) }}</small></span>
                        <span class="h">{{ d.horario }}</span>
                        <span class="a">{{ d.abono ? hhmm(d.abono) : '' }}</span>
                        <i v-if="d.marcacoes" class="bi bi-fingerprint text-info" :title="d.marcacoes + ' marcação(ões) neste dia'"></i>
                      </li>
                      <li v-if="previa.dias.length > 31" class="text-muted small">… e mais {{ previa.dias.length - 31 }} dias</li>
                    </ul>
                    <div v-if="previa.dias.some(d => d.marcacoes && d.util) && !form.parcial" class="small text-muted mt-1">
                      <i class="bi bi-fingerprint text-info"></i> Há marcações em dias abonados: as horas trabalhadas nesses dias contam como extra.
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <template v-if="editavel">
              <button v-if="form.id" class="btn btn-light text-danger me-auto" @click="excluir"><i class="bi bi-trash"></i> Excluir</button>
              <button class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
              <button :class="['btn', podeAprovar ? 'btn-outline-primary' : 'btn-info']" :disabled="salvando" @click="salvar(false)">{{ podeAprovar ? 'Salvar como pendente' : 'Enviar para aprovação' }}</button>
              <button v-if="podeAprovar" class="btn btn-info" :disabled="salvando" @click="salvar(true)"><i class="bi bi-check-lg"></i> Salvar e aprovar</button>
            </template>
            <template v-else>
              <button v-if="form.status === 1 && podeAprovar" class="btn btn-light text-danger me-auto" @click="cancelar(form)">
                <i class="bi bi-arrow-counterclockwise"></i> Cancelar justificativa
              </button>
              <button class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
//Eu sou o caminho, a verdade e a vida. Ninguém vem ao Pai senão por mim. (João 14:6);
import Swal from 'sweetalert2'
import { Modal } from 'bootstrap'
import util from '@/util'

const STATUS = {
  0: { texto: 'Pendente', classe: 'text-bg-warning' },
  1: { texto: 'Aprovada', classe: 'text-bg-success' },
  2: { texto: 'Cancelada', classe: 'text-bg-secondary' },
}
const STATUS_FILTRO = [{ v: '', t: 'Todas' }, { v: '0', t: 'Pendentes' }, { v: '1', t: 'Aprovadas' }, { v: '2', t: 'Canceladas' }]

function iso(d) { return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().substring(0, 10) }
function addDias(dataIso, n) { const d = new Date(dataIso + 'T12:00:00'); d.setDate(d.getDate() + n); return iso(d) }
function pasta() {
  const c = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let s = ''; for (let i = 0; i < 15; i++) s += c.charAt(Math.floor(Math.random() * c.length))
  return s
}

export default {
  name: 'justificativas',
  data() {
    return {
      STATUS, STATUS_FILTRO,
      tipos: [],
      resumo: {},
      lista: [],
      total: 0,
      pagina: 1,
      porPagina: 20,
      filtro: { idcolaborador: '', status: '', tipo: '', datainicio: '', datafim: '' },
      carregando: false,
      salvando: false,
      enviando: false,
      carregandoPrevia: false,
      modal: null,
      form: this.novoForm(),
      arquivos: [],
      previa: null,
      timerPrevia: null,
    }
  },
  computed: {
    podeAprovar() { return util.pode('justificativas_aprovar', this.$store.state.usuario) },
    podeLancar() { return util.pode('justificativas', this.$store.state.usuario) },
    editavel() { return this.podeLancar && (!this.form.id || this.form.status === 0) },
    tipoAtual() { return this.tipos.find(t => t.id === this.form.tipo) },
    pastaArquivos() { return 'arquivos/justificativas/' + this.$store.state.usuario.idempresa + '/' + this.form.patharquivos },
  },
  mounted() {
    const vself = this
    const ajax = {
      url: util.servidor + '/controller/select2sql.php',
      dataType: 'json',
      delay: 250,
      data: (params) => ({ q: params.term, idempresa: vself.$store.state.usuario.idempresa, tabela: 'tbcolaboradores' }),
      processResults: (data) => ({ results: data }),
    }
    $('#just-filtro-colab').select2({ ajax, allowClear: true, placeholder: 'Todos', minimumInputLength: 0, theme: 'bootstrap' })
      .on('change', function () { vself.filtro.idcolaborador = $(this).val() || ''; vself.pesquisar() })
    $('#just-colab').select2({ ajax, placeholder: 'Selecione o colaborador', minimumInputLength: 0, theme: 'bootstrap',
                               dropdownParent: $(this.$refs.modal) })
      .on('change', function () { vself.form.idcolaborador = $(this).val() ? parseInt($(this).val()) : null; vself.atualizarPrevia() })

    this.req('tipos').then(r => { this.tipos = Array.isArray(r.data) ? r.data : [] })
    this.pesquisar()
  },
  beforeUnmount() {
    ['#just-filtro-colab', '#just-colab'].forEach(s => { if ($(s).data('select2')) $(s).select2('destroy') })
    if (this.modal) this.modal.dispose()
  },
  methods: {
    dataBR: (v) => util.dataBR(v),
    dataCurta: (v) => util.dataBR(v).substring(0, 5),
    dataHora(v) { return v ? util.dataBR(v) + ' ' + String(v).substring(11, 16) : '' },
    hhmm(min) { return String(Math.floor(min / 60)).padStart(2, '0') + ':' + String(min % 60).padStart(2, '0') },
    ehImagem(p) { return /\.(jpe?g|png|webp|gif)$/i.test(p) },
    urlArquivo(p) { return util.servidor + '/' + p },
    req(op, extra = []) {
      return util.reqaxios([{ parametro: 'op', valor: op }, { parametro: 'idempresa', valor: this.$store.state.usuario.idempresa }, ...extra],
                           'post', util.servidor + '/controller/justificativas.php')
    },
    novoForm() {
      const hoje = iso(new Date())
      return { id: 0, status: 0, idcolaborador: null, tipo: 0, parcial: false, dtjustificativa: hoje, dtfimjustificativa: hoje,
               hora_inicio: '', hora_fim: '', obs: '', patharquivos: pasta() }
    },

    // ------------------------------------------------------------ lista
    filtroJson() {
      const f = {}
      Object.entries(this.filtro).forEach(([k, v]) => { if (v !== '' && v !== null) f[k] = v })
      return Object.keys(f).length ? JSON.stringify(f) : ''
    },
    filtrarStatus(v) { this.filtro.status = this.filtro.status === v && v !== '' ? '' : v; this.pesquisar() },
    limparFiltros() {
      this.filtro = { idcolaborador: '', status: '', tipo: '', datainicio: '', datafim: '' }
      $('#just-filtro-colab').val(null).trigger('change.select2')
      this.pesquisar()
    },
    async pesquisar() {
      const r = await this.req('qtdregistros', [{ parametro: 'filtro', valor: this.filtroJson() }])
      this.total = r.data && r.data.qtdregistros ? parseInt(r.data.qtdregistros) : 0
      this.carregar(1)
      this.carregarResumo()
    },
    async carregar(pagina) {
      this.pagina = pagina
      this.carregando = true
      try {
        const r = await this.req('listagem', [
          { parametro: 'filtro', valor: this.filtroJson() },
          { parametro: 'limit', valor: this.porPagina }, { parametro: 'offset', valor: (pagina - 1) * this.porPagina },
        ])
        this.lista = Array.isArray(r.data) ? r.data : []
        if (!Array.isArray(r.data)) util.respostaOk(r.data)
      } finally {
        this.carregando = false
      }
    },
    async carregarResumo() {
      const r = await this.req('resumo')
      if (r.data && typeof r.data === 'object') this.resumo = r.data
    },

    // ------------------------------------------------------------ modal
    abrirModal() {
      if (!this.modal) this.modal = new Modal(this.$refs.modal)
      this.modal.show()
    },
    novo() {
      this.form = this.novoForm()
      this.arquivos = []
      this.previa = null
      $('#just-colab').val(null).trigger('change.select2')
      if (this.filtro.idcolaborador) {
        const txt = $('#just-filtro-colab option:selected').text()
        this.form.idcolaborador = parseInt(this.filtro.idcolaborador)
        $('#just-colab').append(new Option(txt, this.filtro.idcolaborador, true, true)).trigger('change.select2')
        this.atualizarPrevia()
      }
      this.abrirModal()
    },
    abrir(j) {
      this.form = {
        ...j, parcial: !!j.hora_inicio, hora_inicio: j.hora_inicio || '', hora_fim: j.hora_fim || '',
        patharquivos: j.patharquivos || pasta(),
      }
      this.arquivos = [...j.arquivos]
      this.previa = null
      $('#just-colab').empty().append(new Option(j.nomecolaborador, j.idcolaborador, true, true)).trigger('change.select2')
      this.atualizarPrevia()
      this.abrirModal()
    },
    setParcial(v) {
      this.form.parcial = v
      if (v) {
        this.form.dtfimjustificativa = this.form.dtjustificativa
        if (!this.form.hora_inicio) { this.form.hora_inicio = '08:00'; this.form.hora_fim = '10:00' }
      }
      this.atualizarPrevia()
    },
    ajustarFim() {
      if (!this.form.dtfimjustificativa || this.form.dtfimjustificativa < this.form.dtjustificativa) this.form.dtfimjustificativa = this.form.dtjustificativa
      this.atualizarPrevia()
    },
    duracao(n) {
      this.form.dtfimjustificativa = addDias(this.form.dtjustificativa, n - 1)
      this.atualizarPrevia()
    },
    atualizarPrevia() {
      clearTimeout(this.timerPrevia)
      this.timerPrevia = setTimeout(() => this.carregarPrevia(), 250)
    },
    async carregarPrevia() {
      const f = this.form
      if (!f.idcolaborador || !f.dtjustificativa) { this.previa = null; return }
      this.carregandoPrevia = true
      try {
        const extra = [
          { parametro: 'idcolaborador', valor: f.idcolaborador }, { parametro: 'tipo', valor: f.tipo }, { parametro: 'id', valor: f.id || 0 },
          { parametro: 'datainicio', valor: f.dtjustificativa }, { parametro: 'datafim', valor: f.parcial ? f.dtjustificativa : f.dtfimjustificativa },
        ]
        if (f.parcial) extra.push({ parametro: 'hora_inicio', valor: f.hora_inicio }, { parametro: 'hora_fim', valor: f.hora_fim })
        const r = await this.req('previa', extra)
        this.previa = r.data && r.data.dias ? r.data : null
      } finally {
        this.carregandoPrevia = false
      }
    },
    async salvar(aprovar) {
      const f = this.form
      if (!f.idcolaborador) return util.notif('error', 'Atenção', 'Selecione o colaborador.')
      if (!f.obs) return util.notif('error', 'Atenção', 'Descreva o motivo na observação.')
      if (f.parcial && (!f.hora_inicio || !f.hora_fim)) return util.notif('error', 'Atenção', 'Informe o horário.')
      this.salvando = true
      try {
        const reg = {
          id: f.id, idcolaborador: f.idcolaborador, tipo: f.tipo, obs: f.obs, patharquivos: f.patharquivos,
          dtjustificativa: f.dtjustificativa, dtfimjustificativa: f.parcial ? f.dtjustificativa : f.dtfimjustificativa,
          hora_inicio: f.parcial ? f.hora_inicio : '', hora_fim: f.parcial ? f.hora_fim : '', aprovar: aprovar ? 1 : 0,
        }
        const r = await this.req('rotinacrud', [{ parametro: 'opcrud', valor: f.id ? 2 : 1 }, { parametro: 'registro', valor: JSON.stringify(reg) }])
        if (util.respostaOk(r.data)) {
          this.modal.hide()
          util.notif('success', aprovar ? 'Justificativa aprovada' : 'Justificativa salva como pendente', '')
          this.pesquisar()
        }
      } finally {
        this.salvando = false
      }
    },
    async excluir() {
      const ok = await Swal.fire({ title: 'Excluir esta justificativa?', text: 'Ela ainda está pendente, então pode ser apagada.', icon: 'warning',
                                   showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Voltar' })
      if (!ok.isConfirmed) return
      const r = await this.req('rotinacrud', [{ parametro: 'opcrud', valor: 3 }, { parametro: 'registro', valor: JSON.stringify({ id: this.form.id }) }])
      if (util.respostaOk(r.data)) { this.modal.hide(); this.pesquisar() }
    },

    // ------------------------------------------------------- aprovação
    async aprovar(j) {
      const ok = await Swal.fire({
        title: 'Aprovar justificativa?',
        html: `<b>${j.nomecolaborador}</b><br>${j.tipo_nome}${j.abona ? ' · abona ' + j.abonadas : ''}`,
        icon: 'question', showCancelButton: true, confirmButtonText: 'Aprovar', cancelButtonText: 'Voltar',
      })
      if (!ok.isConfirmed) return
      const r = await this.req('efetivar', [{ parametro: 'id', valor: j.id }])
      if (util.respostaOk(r.data)) { util.notif('success', 'Aprovada', ''); this.pesquisar() }
    },
    async cancelar(j, recusar = false) {
      const res = await Swal.fire({
        title: recusar ? 'Recusar justificativa?' : 'Cancelar justificativa aprovada?',
        text: recusar ? 'Os dias continuam como falta no espelho.' : 'Os dias voltam a contar como falta no espelho. O registro fica guardado.',
        input: 'text', inputPlaceholder: 'Motivo (obrigatório)', inputValidator: (v) => (!v || !v.trim() ? 'Informe o motivo.' : undefined),
        icon: 'warning', showCancelButton: true, confirmButtonText: recusar ? 'Recusar' : 'Cancelar justificativa', cancelButtonText: 'Voltar',
      })
      if (!res.isConfirmed) return
      const r = await this.req('cancelar', [{ parametro: 'id', valor: j.id }, { parametro: 'motivo', valor: res.value.trim() }])
      if (util.respostaOk(r.data)) {
        if (this.modal) this.modal.hide()
        util.notif('success', recusar ? 'Recusada' : 'Cancelada', '')
        this.pesquisar()
      }
    },

    // ---------------------------------------------------------- anexos
    async enviarArquivos(ev) {
      const files = [...ev.target.files]
      ev.target.value = ''
      if (!files.length) return
      this.enviando = true
      try {
        const params = [{ parametro: 'localsalvar', valor: this.pastaArquivos }, { parametro: 'autonumerar', valor: '1' }]
        files.forEach(f => params.push({ parametro: 'arquivos[]', valor: f }))
        const r = await util.reqaxios(params, 'post', util.servidor + '/controller/uploadfile.php')
        if (r.data && Array.isArray(r.data.arquivos)) this.arquivos = r.data.arquivos
        if (r.data && r.data.erros && r.data.erros.length) util.notif('error', 'Alguns arquivos não foram enviados', r.data.erros.join('\n'))
      } finally {
        this.enviando = false
      }
    },
    async removerArquivo(a) {
      const r = await util.reqaxios([
        { parametro: 'op', valor: 'excluirarquivo' }, { parametro: 'patharquivo', valor: a.patharquivo },
        { parametro: 'localarquivo', valor: this.pastaArquivos },
      ], 'post', util.servidor + '/controller/utilitario.php')
      const d = typeof r.data === 'string' ? JSON.parse(r.data || '{}') : r.data
      if (d && Array.isArray(d.arquivos)) this.arquivos = d.arquivos
      else util.notif('error', 'Atenção', (d && d.inf) || 'Não foi possível remover o arquivo.')
    },
  },
}
</script>

<style scoped lang="scss">
.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
.kpi {
  background: #fff; border: 0; border-radius: 12px; box-shadow: var(--ck-sombra); padding: 10px 14px; text-align: left;
  border-left: 4px solid var(--ck-borda);
}
.kpi span { display: block; font-size: .72rem; color: var(--ck-texto-2); font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
.kpi strong { font-size: 1.35rem; font-variant-numeric: tabular-nums; }
.kpi.alerta { border-left-color: var(--ck-alerta); cursor: pointer; }
.kpi.alerta.ativo { box-shadow: 0 0 0 2px var(--ck-alerta); }
.kpi.ok { border-left-color: var(--ck-sucesso); }
.status-filtro .btn { min-width: 92px; }
.lista th { font-size: .72rem; text-transform: uppercase; color: var(--ck-texto-2); background: #f7faf9; letter-spacing: .04em; }
.lista tbody tr { cursor: pointer; }
.lista tr.cancelada td { color: #93a29d; }
.lista .tipo { font-size: .85rem; }
.num { font-variant-numeric: tabular-nums; }
.atalhos { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.anexos { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.anexo { position: relative; width: 84px; height: 84px; }
.anexo .miniatura {
  width: 100%; height: 100%; border-radius: 10px; border: 1px solid var(--ck-borda); display: flex; align-items: center; justify-content: center;
  overflow: hidden; background: #f7faf9; color: #c0392b; font-size: 2rem;
}
.anexo .miniatura img { width: 100%; height: 100%; object-fit: cover; }
.btn-remover {
  position: absolute; top: -7px; right: -7px; width: 22px; height: 22px; border-radius: 50%; border: 0; background: #c0392b; color: #fff;
  display: flex; align-items: center; justify-content: center; font-size: .9rem; padding: 0;
}
.anexo.adicionar {
  border: 1.5px dashed var(--ck-borda); border-radius: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: var(--ck-primaria); cursor: pointer; margin: 0; font-size: 1.3rem;
}
.anexo.adicionar small { font-size: .7rem; font-weight: 600; }
.anexo.adicionar:hover { background: var(--ck-primaria-suave); }
.auditoria { list-style: none; padding: 10px 12px; margin: 4px 0 0; background: #f7faf9; border-radius: 10px; font-size: .8rem; color: var(--ck-texto-2); }
.auditoria li { padding: 2px 0; }
.previa { background: #f7faf9; border: 1px solid var(--ck-borda); border-radius: 12px; padding: 12px 14px; height: 100%; }
.totais { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.totais div { background: #fff; border-radius: 10px; padding: 8px 10px; }
.totais span { display: block; font-size: .68rem; text-transform: uppercase; color: var(--ck-texto-2); font-weight: 600; letter-spacing: .04em; }
.totais strong { font-size: 1.15rem; font-variant-numeric: tabular-nums; }
.dias { list-style: none; padding: 0; margin: 10px 0 0; max-height: 320px; overflow-y: auto; font-size: .8rem; }
.dias li { display: flex; gap: 8px; align-items: center; padding: 4px 2px; border-bottom: 1px solid var(--ck-borda); }
.dias li.folga { color: #93a29d; }
.dias .d { width: 70px; font-weight: 600; font-variant-numeric: tabular-nums; }
.dias .d small { font-weight: 500; color: var(--ck-texto-2); }
.dias .h { flex: 1; }
.dias .a { font-weight: 700; color: var(--ck-sucesso); font-variant-numeric: tabular-nums; }
</style>
